
import React, { useEffect, useState, useRef } from "react";
import { Users, Clock, LineChart, Package } from "lucide-react";
import MixpanelService from "@/services/mixpanel";

type TrustIndicator = {
  icon: React.ReactNode;
  number: number | string;
  label: string;
  showPlus?: boolean;
};

const trustIndicators: TrustIndicator[] = [
  {
    icon: <Users className="w-6 h-6 text-primary" />,
    number: 40,
    label: "New Testers Monthly",
    showPlus: false
  },
  {
    icon: <Clock className="w-6 h-6 text-primary" />,
    number: "4-10",
    label: "Results in Weeks",
    showPlus: false
  },
  {
    icon: <LineChart className="w-6 h-6 text-primary" />,
    number: 50000,
    label: "Newsletter Reach",
    showPlus: true
  },
  {
    icon: <Package className="w-6 h-6 text-primary" />,
    number: 1000,
    label: "Products Tested past 12 months",
    showPlus: true
  }
];

// Easing function for smooth animation
const easeOutExpo = (x: number): number => {
  return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
};

const TrustIndicators: React.FC = () => {
  const [progress, setProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const hasTracked = useRef(false);

  useEffect(() => {
    const duration = 2000; // 2 seconds
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const newProgress = Math.min(elapsed / duration, 1);

      // Apply easing
      const easedProgress = easeOutExpo(newProgress);
      setProgress(easedProgress);

      if (newProgress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    // Set up intersection observer to track when trust indicators are viewed
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !hasTracked.current) {
          // Track that the user has seen the trust indicators
          MixpanelService.trackAction('Trust Indicators Viewed', {
            indicators: trustIndicators.map(indicator => indicator.label),
            section: 'Trust Indicators'
          });
          hasTracked.current = true;
        }
      },
      { threshold: 0.6 } // Fire when 60% of the section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-neutral-100 py-10">
      <div className="container">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12">
          {trustIndicators.map((indicator, index) => (
            <div key={index} className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                {indicator.icon}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl md:text-4xl font-bold text-neutral-900">
                  {typeof indicator.number === 'string' 
                    ? indicator.number 
                    : Math.round(indicator.number * progress) >= 1000
                      ? Math.round(indicator.number * progress).toLocaleString()
                      : Math.round(indicator.number * progress)}
                </span>
                {indicator.showPlus && <span className="text-3xl md:text-4xl font-bold">+</span>}
              </div>
              <p className="text-sm md:text-base text-neutral-700">{indicator.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustIndicators;
